import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Globe, Loader2, Check, X, AlertCircle, Camera, Users } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function EnrichmentModal({ open, onOpenChange, recordType, recordIds, onComplete }) {
    const { getAuthHeader } = useAuth();
    const [method, setMethod] = useState('ai'); // 'ai', 'apify', or 'linkedin_photo'
    const [loading, setLoading] = useState(false);
    const [suggestions, setSuggestions] = useState(null);
    const [applying, setApplying] = useState(false);
    const [selectedSuggestions, setSelectedSuggestions] = useState({});
    const [photoResults, setPhotoResults] = useState(null);

    const runAIEnrich = async () => {
        setLoading(true);
        setSuggestions(null);
        try {
            const res = await axios.post(`${API}/api/enrichment/ai`, {
                record_type: recordType,
                record_ids: recordIds,
                fields_to_enrich: [],
            }, { headers: getAuthHeader() });

            const enriched = res.data.enriched || [];
            setSuggestions(enriched);

            // Auto-select high-confidence suggestions
            const autoSelected = {};
            enriched.forEach(rec => {
                const recId = rec.record_id;
                autoSelected[recId] = {};
                Object.entries(rec.suggestions || {}).forEach(([field, data]) => {
                    if (data.confidence >= 70) {
                        autoSelected[recId][field] = true;
                    }
                });
            });
            setSelectedSuggestions(autoSelected);

            if (enriched.length === 0) {
                toast.info('No enrichment suggestions found');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Enrichment failed');
        }
        finally { setLoading(false); }
    };

    const runApifyEnrich = async () => {
        setLoading(true);
        try {
            const res = await axios.post(`${API}/api/enrichment/apify`, {
                record_type: recordType,
                record_ids: recordIds,
            }, { headers: getAuthHeader() });
            toast.success(`Scraping queued: ${res.data.targets_count} targets. Task ID: ${res.data.task_id}`);
            onOpenChange(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Apify enrichment failed');
        }
        finally { setLoading(false); }
    };

    const runLinkedInPhotoEnrich = async () => {
        setLoading(true);
        setPhotoResults(null);
        try {
            const res = await axios.post(`${API}/api/enrichment/linkedin-photos/bulk`, {
                contact_ids: recordIds,
            }, { headers: getAuthHeader() });
            setPhotoResults(res.data);
            const { photos_uploaded, faces_indexed, re_indexed, failed, skipped } = res.data;
            if (photos_uploaded > 0 || re_indexed > 0) {
                toast.success(`Photos: ${photos_uploaded} uploaded, ${faces_indexed} faces indexed, ${re_indexed} re-indexed`);
            } else if (failed > 0 || skipped > 0) {
                toast.warning(`No photos enriched. ${failed} failed, ${skipped} skipped (no LinkedIn URL).`);
            } else {
                toast.info('No photos to enrich');
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'LinkedIn photo enrichment failed');
        }
        finally { setLoading(false); }
    };

    const toggleSuggestion = (recordId, field) => {
        setSelectedSuggestions(prev => ({
            ...prev,
            [recordId]: {
                ...(prev[recordId] || {}),
                [field]: !(prev[recordId] || {})[field],
            }
        }));
    };

    const applySuggestions = async () => {
        if (!suggestions) return;
        setApplying(true);
        try {
            const updates = [];
            suggestions.forEach(rec => {
                const fields = {};
                Object.entries(rec.suggestions || {}).forEach(([field, data]) => {
                    if ((selectedSuggestions[rec.record_id] || {})[field]) {
                        fields[field] = data.value;
                    }
                });
                if (Object.keys(fields).length > 0) {
                    updates.push({ record_id: rec.record_id, record_type: recordType, fields });
                }
            });

            if (updates.length === 0) {
                toast.error('No suggestions selected');
                setApplying(false);
                return;
            }

            const res = await axios.post(`${API}/api/enrichment/ai/apply`, { updates }, { headers: getAuthHeader() });
            toast.success(`Applied enrichment to ${res.data.applied} records`);
            onOpenChange(false);
            if (onComplete) onComplete();
        } catch (err) {
            toast.error('Failed to apply enrichment');
        }
        finally { setApplying(false); }
    };

    const selectedCount = Object.values(selectedSuggestions).reduce(
        (total, fields) => total + Object.values(fields).filter(Boolean).length, 0
    );

    const confidenceColor = (c) => {
        if (c >= 80) return 'text-emerald-600 bg-emerald-50';
        if (c >= 60) return 'text-amber-600 bg-amber-50';
        return 'text-red-600 bg-red-50';
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Enrich {recordIds.length} {recordType}{recordIds.length > 1 ? 's' : ''}</DialogTitle>
                    <DialogDescription>Use AI or web scraping to fill in missing data.</DialogDescription>
                </DialogHeader>

                {/* Method Selection */}
                {!suggestions && !loading && !photoResults && (
                    <div className="space-y-4">
                        <div className={`grid gap-3 ${recordType === 'contact' ? 'grid-cols-3' : 'grid-cols-2'}`}>
                            <button
                                onClick={() => setMethod('ai')}
                                className={`p-4 rounded-lg border-2 text-left transition-colors ${
                                    method === 'ai' ? 'border-stone-900 bg-stone-50' : 'border-stone-200 hover:border-stone-300'
                                }`}
                            >
                                <Sparkles className="w-5 h-5 text-stone-700 mb-2" />
                                <h4 className="text-sm font-semibold text-stone-900">AI Enrichment</h4>
                                <p className="text-xs text-stone-500 mt-1">Uses AI to infer missing fields from existing data context. Instant results.</p>
                            </button>
                            <button
                                onClick={() => setMethod('apify')}
                                className={`p-4 rounded-lg border-2 text-left transition-colors ${
                                    method === 'apify' ? 'border-stone-900 bg-stone-50' : 'border-stone-200 hover:border-stone-300'
                                }`}
                            >
                                <Globe className="w-5 h-5 text-stone-700 mb-2" />
                                <h4 className="text-sm font-semibold text-stone-900">Web Scraping (Apify)</h4>
                                <p className="text-xs text-stone-500 mt-1">Scrapes websites and LinkedIn for real data. Requires Apify API key.</p>
                            </button>
                            {recordType === 'contact' && (
                                <button
                                    onClick={() => setMethod('linkedin_photo')}
                                    className={`p-4 rounded-lg border-2 text-left transition-colors ${
                                        method === 'linkedin_photo' ? 'border-stone-900 bg-stone-50' : 'border-stone-200 hover:border-stone-300'
                                    }`}
                                >
                                    <Camera className="w-5 h-5 text-stone-700 mb-2" />
                                    <h4 className="text-sm font-semibold text-stone-900">Profile Photos</h4>
                                    <p className="text-xs text-stone-500 mt-1">Download LinkedIn photos, store in S3, and index for face matching.</p>
                                </button>
                            )}
                        </div>

                        <div className="flex justify-end">
                            <Button onClick={method === 'ai' ? runAIEnrich : method === 'linkedin_photo' ? runLinkedInPhotoEnrich : runApifyEnrich}>
                                {method === 'ai' ? <><Sparkles className="w-4 h-4 mr-1.5" /> Run AI Enrichment</> :
                                method === 'linkedin_photo' ? <><Camera className="w-4 h-4 mr-1.5" /> Enrich Photos</> :
                                <><Globe className="w-4 h-4 mr-1.5" /> Start Web Scraping</>}
                            </Button>
                        </div>
                    </div>
                )}

                {/* Loading */}
                {loading && (
                    <div className="flex flex-col items-center justify-center py-10">
                        <Loader2 className="w-8 h-8 text-stone-400 animate-spin mb-3" />
                        <p className="text-sm text-stone-500">
                            {method === 'ai' ? 'AI is analyzing your records...' :
                             method === 'linkedin_photo' ? 'Downloading profile photos and indexing faces...' :
                             'Queuing web scraping job...'}
                        </p>
                        {method === 'linkedin_photo' && (
                            <p className="text-xs text-stone-400 mt-1">This may take 1-3 minutes for multiple contacts</p>
                        )}
                    </div>
                )}

                {/* LinkedIn Photo Results */}
                {photoResults && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-4 gap-3">
                            <div className="bg-emerald-50 rounded-lg p-3 text-center">
                                <p className="text-lg font-bold text-emerald-700">{photoResults.photos_uploaded}</p>
                                <p className="text-xs text-emerald-600">Photos Uploaded</p>
                            </div>
                            <div className="bg-blue-50 rounded-lg p-3 text-center">
                                <p className="text-lg font-bold text-blue-700">{photoResults.faces_indexed}</p>
                                <p className="text-xs text-blue-600">Faces Indexed</p>
                            </div>
                            <div className="bg-stone-50 rounded-lg p-3 text-center">
                                <p className="text-lg font-bold text-stone-700">{photoResults.re_indexed}</p>
                                <p className="text-xs text-stone-600">Re-indexed</p>
                            </div>
                            <div className="bg-amber-50 rounded-lg p-3 text-center">
                                <p className="text-lg font-bold text-amber-700">{(photoResults.failed || 0) + (photoResults.skipped || 0)}</p>
                                <p className="text-xs text-amber-600">Skipped/Failed</p>
                            </div>
                        </div>

                        {photoResults.details && photoResults.details.length > 0 && (
                            <div className="space-y-1.5 max-h-[300px] overflow-y-auto">
                                {photoResults.details.map((d, i) => (
                                    <div key={i} className="flex items-center gap-2 py-1.5 px-3 bg-stone-50 rounded-lg text-xs">
                                        {d.status === 'enriched' || d.status === 're_indexed' ? (
                                            <Check className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                        ) : (
                                            <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                                        )}
                                        <span className="text-stone-600 truncate flex-1">{d.contact_id}</span>
                                        <span className={`px-2 py-0.5 rounded-full font-medium ${
                                            d.status === 'enriched' ? 'bg-emerald-100 text-emerald-700' :
                                            d.status === 're_indexed' ? 'bg-blue-100 text-blue-700' :
                                            'bg-amber-100 text-amber-700'
                                        }`}>{d.status.replace(/_/g, ' ')}</span>
                                        {d.face_indexed && <span className="text-emerald-500 text-[10px]">Face OK</span>}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="flex justify-end pt-2 border-t border-stone-100">
                            <Button size="sm" onClick={() => { setPhotoResults(null); onOpenChange(false); if (onComplete) onComplete(); }}>
                                Done
                            </Button>
                        </div>
                    </div>
                )}

                {/* AI Suggestions Review */}
                {suggestions && (
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-stone-600">{suggestions.length} record(s) with suggestions</p>
                            <span className="text-xs text-stone-400">{selectedCount} field(s) selected</span>
                        </div>

                        <div className="space-y-3 max-h-[400px] overflow-y-auto">
                            {suggestions.map(rec => (
                                <div key={rec.record_id} className="bg-stone-50 rounded-lg p-3 space-y-2">
                                    <p className="text-xs font-medium text-stone-500 truncate">{rec.record_id}</p>
                                    {Object.entries(rec.suggestions || {}).length === 0 ? (
                                        <p className="text-xs text-stone-400 italic">No suggestions for this record</p>
                                    ) : (
                                        Object.entries(rec.suggestions || {}).map(([field, data]) => (
                                            <label key={field} className="flex items-center gap-3 py-1.5 px-2 rounded-lg bg-white cursor-pointer hover:bg-stone-50">
                                                <input
                                                    type="checkbox"
                                                    checked={(selectedSuggestions[rec.record_id] || {})[field] || false}
                                                    onChange={() => toggleSuggestion(rec.record_id, field)}
                                                    className="rounded border-stone-300"
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs font-medium text-stone-700 capitalize">{field.replace('_', ' ')}</span>
                                                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${confidenceColor(data.confidence)}`}>
                                                            {data.confidence}%
                                                        </span>
                                                    </div>
                                                    <p className="text-sm text-stone-900 font-medium">{data.value}</p>
                                                    {data.reasoning && <p className="text-xs text-stone-400 mt-0.5">{data.reasoning}</p>}
                                                </div>
                                            </label>
                                        ))
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="flex items-center justify-between pt-2 border-t border-stone-100">
                            <Button variant="outline" size="sm" onClick={() => { setSuggestions(null); setSelectedSuggestions({}); }}>
                                <X className="w-3.5 h-3.5 mr-1" /> Cancel
                            </Button>
                            <Button size="sm" onClick={applySuggestions} disabled={applying || selectedCount === 0}>
                                {applying ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Applying...</> :
                                <><Check className="w-3.5 h-3.5 mr-1" /> Apply {selectedCount} Suggestion{selectedCount !== 1 ? 's' : ''}</>}
                            </Button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
